import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, filter, map, switchMap, take } from 'rxjs';
import { AuthService } from './auth.service';
import { UserProxy } from '../store/user/user.proxy';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard {
  constructor(
    private authService: AuthService,
    private userProxy: UserProxy,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.checkRoleAccess(route, state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.checkRoleAccess(route, state);
  }

  private checkRoleAccess(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.authService.isDoneLoading$.pipe(
      filter(isDone => isDone),
      switchMap(() => this.authService.isAuthenticated$),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          this.authService.login(state.url);
          return new Observable<boolean>(observer => observer.next(false));
        }
        
        // Get allowed roles from route data (check parent route if child doesn't have roles)
        let allowedRoles: string[] = route.data['roles'];
        
        // If child route doesn't have roles, check parent route
        if (!allowedRoles && route.parent) {
          allowedRoles = route.parent.data['roles'];
        }
        
        // If no roles specified or empty array, allow everyone
        if (!allowedRoles || allowedRoles.length === 0) {
          return new Observable<boolean>(observer => {
            observer.next(true);
            observer.complete();
          });
        }
        
        // Check if user has one of the allowed roles
        return this.userProxy.userRoles$.pipe(
          filter(userRoles => userRoles && userRoles.length > 0),
          take(1),
          map(userRoles => {
            const hasAccess = userRoles.some(userRole => allowedRoles.includes(userRole));
            
            if (!hasAccess) {
              // Navigate to unauthorized page with return URL
              return this.router.createUrlTree(['/unauthorized'], {
                queryParams: { returnUrl: state.url }
              });
            }
            
            return true;
          })
        );
      })
    );
  }
}
